import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ChevronRight, Search, Loader2, Phone, Mail, Wrench, Package, Cog, ArrowRight } from 'lucide-react';

// Catalogue pièces : données publiques du catalogue Easy Pièces (aucun prix exposé).
const EP_BASE = 'https://easypieces.fr';
const EP_API = `${EP_BASE}/api/parts-catalogue`;
const PAGE_SIZE = 24;

const slugify = (s) => (s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
const fmt = (n) => (n ?? 0).toLocaleString('fr-FR');

async function epFetch(params) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null));
  const r = await fetch(`${EP_API}?${qs.toString()}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

function PartCard({ p }) {
  const models = p.engine_models || [];
  const title = [p.designation, p.variant].filter(Boolean).join(' — ');
  const message = `Bonjour, je souhaite un prix et la disponibilité pour la pièce suivante :\n${title}\nRéf. ${p.public_ref}` +
    (p.engine_brand ? `\nMoteur : ${p.engine_brand}${models.length ? ' ' + models.slice(0, 5).join(', ') : ''}` : '') +
    (p.machine_brand ? `\nMachine : ${p.machine_brand}${(p.machine_models || []).length ? ' ' + p.machine_models.slice(0, 5).join(', ') : ''}` : '') +
    `\n\nModèle / numéro de série de ma machine : `;
  const contactUrl = `/portail/contact?sujet=sav&message=${encodeURIComponent(message)}`;
  const rows = [
    ['Famille', p.family_label],
    ['Moteur', p.engine_brand ? `${p.engine_brand}${models.length ? ' · ' + models.slice(0, 4).join(', ') + (models.length > 4 ? ` +${models.length - 4}` : '') : ''}` : ''],
    ['Machine', p.machine_brand ? `${p.machine_brand}${(p.machine_models || []).length ? ' · ' + p.machine_models.slice(0, 3).join(', ') : ''}` : ''],
    ['Réf. techniques', (p.technical_refs || []).slice(0, 3).join(', ')],
  ].filter(([, v]) => v);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition flex flex-col overflow-hidden">
      <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center overflow-hidden">
        {p.image ? (
          <img src={`${EP_BASE}${p.image}`} alt={title} loading="lazy" className="w-full h-full object-contain" />
        ) : (
          <Cog className="w-12 h-12 text-slate-300" />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-bold text-slate-800 leading-snug" style={{ fontFamily: 'Barlow Condensed', fontSize: '1.15rem' }}>{title}</h3>
        <p className="text-xs text-slate-400 mb-3">Réf. {p.public_ref}</p>
        <dl className="text-sm space-y-1.5 mb-4">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-slate-100 pb-1">
              <dt className="text-slate-500 shrink-0">{k}</dt>
              <dd className="text-slate-800 font-medium text-right">{v}</dd>
            </div>
          ))}
        </dl>
        {p.image_kind === 'illustration' && <p className="text-[11px] text-slate-400 mb-3">Photo générique de la famille.</p>}
        <Link to={contactUrl} className="mt-auto inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold text-sm rounded-lg py-2.5 transition">
          Demander un prix <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

export default function PublicPartsPage() {
  const { settings } = useOutletContext() || {};
  const { brand: brandSlug } = useParams();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);            // catalogue complet : catégories, marques, total
  const [brandCounts, setBrandCounts] = useState({});
  const [category, setCategory] = useState('');
  const [family, setFamily] = useState('');
  const [engineModel, setEngineModel] = useState('');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const brands = meta?.facets?.engine_brand || [];
  const brand = useMemo(() => brands.find((b) => slugify(b) === brandSlug) || '', [brands, brandSlug]);

  // Catalogue global + nombre de pièces par marque moteur (barre latérale)
  useEffect(() => {
    let alive = true;
    epFetch({ limit: 1 }).then(async (d) => {
      if (!alive) return;
      setMeta(d);
      const counts = {};
      await Promise.all((d.facets?.engine_brand || []).map((b) =>
        epFetch({ engine_brand: b, limit: 1 }).then((r) => { counts[b] = r.total; }).catch(() => {})));
      if (alive) setBrandCounts(counts);
    }).catch(() => alive && setError("Le catalogue des pièces est momentanément indisponible."));
    return () => { alive = false; };
  }, []);

  // Réinitialise les filtres quand on change de marque
  useEffect(() => { setFamily(''); setEngineModel(''); setPage(1); }, [brandSlug]);
  useEffect(() => { setPage(1); }, [category, family, engineModel, q]);

  useEffect(() => {
    if (!meta) return;
    if (brandSlug && !brand) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    epFetch({ engine_brand: brand, category, family, engine_model: engineModel, q, page, limit: PAGE_SIZE })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setItems((prev) => (page === 1 ? d.items : [...prev, ...d.items]));
        setError('');
      })
      .catch(() => alive && setError("Le catalogue des pièces est momentanément indisponible."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [meta, brand, brandSlug, category, family, engineModel, q, page]);

  const categories = meta?.categories || [];
  const familiesOfCategory = (categories.find((c) => c.id === category)?.families || []);
  const familyOptions = category ? familiesOfCategory : categories.flatMap((c) => c.families);
  const availableFamilies = new Set(data?.facets?.family || []);
  const engineModels = data?.facets?.engine_models || [];
  const title = brand ? `Pièces moteur ${brand}` : 'Pièces moteurs & engins TP';
  const phone = settings?.contact_phone || settings?.phone || '+262 692 04 20 73';
  const email = settings?.contact_email || settings?.email || 'contact@idmatoi.com';

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Fil d'Ariane */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 text-sm text-slate-500 flex items-center gap-1.5 flex-wrap">
          <Link to="/portail" className="hover:text-yellow-600">Accueil</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/portail/parts" className={brand ? 'hover:text-yellow-600' : 'text-slate-800 font-medium'}>Parts</Link>
          {brand && (<><ChevronRight className="w-3.5 h-3.5" /><span className="text-slate-800 font-medium">{brand}</span></>)}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 grid lg:grid-cols-[240px_1fr] gap-8">
        {/* Barre latérale : marques moteur */}
        <aside className="hidden lg:block lg:sticky lg:top-24 self-start">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <h2 className="bg-slate-900 text-white font-bold px-4 py-3 text-sm tracking-wide">MARQUES MOTEUR</h2>
            <nav className="py-1 max-h-[60vh] overflow-y-auto">
              <Link to="/portail/parts" className={`flex justify-between px-4 py-2 text-sm hover:bg-yellow-50 ${!brand ? 'text-yellow-600 font-semibold bg-yellow-50' : 'text-slate-700'}`}>
                <span>Toutes les marques</span><span className="text-slate-400">{fmt(meta?.total)}</span>
              </Link>
              {brands.map((b) => (
                <Link key={b} to={`/portail/parts/${slugify(b)}`} className={`flex justify-between px-4 py-2 text-sm hover:bg-yellow-50 ${brand === b ? 'text-yellow-600 font-semibold bg-yellow-50' : 'text-slate-700'}`}>
                  <span>{b}</span><span className="text-slate-400">{brandCounts[b] !== undefined ? fmt(brandCounts[b]) : ''}</span>
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        <main className="min-w-0">
          {/* Bandeau */}
          <section className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 mb-6 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-1.5 bg-yellow-500" />
            <p className="text-yellow-500 font-semibold text-sm mb-1 tracking-wide">PIÈCES DÉTACHÉES</p>
            <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: 'Barlow Condensed' }}>{title}</h1>
            <p className="text-slate-300 max-w-2xl text-sm md:text-base">
              {brand
                ? `Pièces moteur et d'entretien compatibles ${brand} pour engins de chantier. Indiquez-nous le modèle et le numéro de série de votre machine : nous confirmons la référence, le prix et le délai.`
                : "Moteurs, culasses, pièces internes, injection, refroidissement, électricité et train de roulement pour mini-pelles, pelles et engins TP. Prix et disponibilité sur demande, livraison à La Réunion et à Mayotte."}
            </p>
            <div className="grid grid-cols-3 gap-3 mt-6 max-w-xl">
              {[[fmt(brand ? brandCounts[brand] ?? data?.total : meta?.total), 'pièces référencées'],
                [fmt(brand ? engineModels.length : (meta?.facets?.engine_models || []).length), 'modèles de moteurs'],
                [brand ? fmt((data?.facets?.family || []).length) : fmt(brands.length), brand ? 'familles de pièces' : 'marques moteur']].map(([v, l]) => (
                <div key={l} className="bg-white/5 border-t-2 border-yellow-500 rounded-lg px-3 py-3">
                  <p className="text-2xl font-bold">{v || '—'}</p>
                  <p className="text-xs text-slate-400">{l}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Catégories */}
          <section className="mb-6">
            <h2 className="text-lg font-bold text-slate-800 mb-3">Catégories</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button onClick={() => { setCategory(''); setFamily(''); }} className={`text-left rounded-xl border p-3 transition ${!category ? 'border-yellow-500 bg-yellow-50' : 'border-slate-200 bg-white hover:border-yellow-400'}`}>
                <Package className="w-5 h-5 text-yellow-500 mb-1" />
                <p className="font-semibold text-sm text-slate-800">Toutes les pièces</p>
              </button>
              {categories.map((c) => (
                <button key={c.id} onClick={() => { setCategory(c.id); setFamily(''); }} className={`text-left rounded-xl border p-3 transition ${category === c.id ? 'border-yellow-500 bg-yellow-50' : 'border-slate-200 bg-white hover:border-yellow-400'}`}>
                  <Wrench className="w-5 h-5 text-yellow-500 mb-1" />
                  <p className="font-semibold text-sm text-slate-800">{c.label}</p>
                  <p className="text-xs text-slate-400">{c.families.length} familles</p>
                </button>
              ))}
            </div>
          </section>

          {/* Recherche et filtres */}
          <section className="bg-white rounded-xl border border-slate-200 p-4 mb-6 grid md:grid-cols-[1fr_220px_200px] gap-3">
            <select value={brand ? slugify(brand) : ''} onChange={(e) => navigate(e.target.value ? `/portail/parts/${e.target.value}` : '/portail/parts')} className="lg:hidden border border-slate-200 rounded-lg text-sm px-3 py-2.5 bg-white">
              <option value="">Toutes les marques moteur</option>
              {brands.map((b) => <option key={b} value={slugify(b)}>{b}{brandCounts[b] !== undefined ? ` (${fmt(brandCounts[b])})` : ''}</option>)}
            </select>
            <form onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); }} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Désignation, référence, modèle de moteur…" className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500" />
              </div>
              <button type="submit" className="bg-slate-900 text-white text-sm font-medium rounded-lg px-4">Rechercher</button>
            </form>
            <select value={family} onChange={(e) => setFamily(e.target.value)} className="border border-slate-200 rounded-lg text-sm px-3 py-2.5 bg-white">
              <option value="">Toutes les familles</option>
              {familyOptions.filter((f) => !data || availableFamilies.has(f.id) || f.id === family).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
            <select value={engineModel} onChange={(e) => setEngineModel(e.target.value)} className="border border-slate-200 rounded-lg text-sm px-3 py-2.5 bg-white">
              <option value="">Tous les modèles de moteur</option>
              {engineModels.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </section>

          {/* Résultats */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-800">{data ? `${fmt(data.total)} pièce${data.total > 1 ? 's' : ''}` : 'Pièces'}</h2>
            {(q || family || engineModel || category) && (
              <button onClick={() => { setQ(''); setQInput(''); setFamily(''); setEngineModel(''); setCategory(''); }} className="text-sm text-yellow-600 hover:underline">Effacer les filtres</button>
            )}
          </div>

          {error && <div className="bg-red-50 text-red-700 rounded-lg p-4 text-sm mb-4">{error}</div>}
          {brandSlug && meta && !brand && (
            <div className="bg-white rounded-xl border p-6 text-sm text-slate-600">Marque inconnue. <button className="text-yellow-600 underline" onClick={() => navigate('/portail/parts')}>Voir toutes les pièces</button></div>
          )}

          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map((p) => <PartCard key={p.id} p={p} />)}
          </div>

          {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-yellow-500" /></div>}
          {!loading && data && items.length === 0 && (
            <div className="bg-white rounded-xl border p-8 text-center text-slate-600 text-sm">
              Aucune pièce ne correspond à cette recherche. Envoyez-nous votre demande : nous recherchons la pièce pour vous.
            </div>
          )}
          {!loading && data && items.length < data.total && (
            <div className="flex justify-center mt-6">
              <button onClick={() => setPage((x) => x + 1)} className="bg-white border border-slate-300 hover:border-yellow-500 rounded-lg px-6 py-2.5 text-sm font-medium text-slate-700">
                Afficher plus de pièces ({fmt(data.total - items.length)} restantes)
              </button>
            </div>
          )}

          <p className="text-xs text-slate-400 mt-6">Compatibilités indiquées à titre indicatif, à confirmer avec le modèle et le numéro de série de votre machine. Photos non contractuelles. Prix et disponibilité sur devis.</p>

          {/* Demande */}
          <section className="mt-10 bg-yellow-500 rounded-2xl p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow Condensed' }}>Vous ne trouvez pas votre pièce ?</h2>
              <p className="text-slate-800 text-sm">Donnez-nous la marque, le modèle et le numéro de série de votre machine ou de votre moteur : nous identifions la pièce et vous envoyons un devis.</p>
              <div className="flex flex-wrap gap-4 mt-3 text-sm font-medium text-slate-900">
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4" />{phone}</a>
                <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4" />{email}</a>
              </div>
            </div>
            <Link to={`/portail/contact?sujet=sav&message=${encodeURIComponent('Bonjour, je recherche la pièce suivante :\n\nMarque / modèle de machine : \nMoteur : \nNuméro de série : ')}`} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg px-6 py-3 text-center">
              Demander une pièce
            </Link>
          </section>

          {/* Autres marques */}
          {brands.length > 0 && (
            <section className="mt-10">
              <h2 className="text-lg font-bold text-slate-800 mb-3">{brand ? 'Autres marques moteur' : 'Pièces par marque moteur'}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {brands.filter((b) => b !== brand).map((b) => (
                  <Link key={b} to={`/portail/parts/${slugify(b)}`} onClick={() => window.scrollTo(0, 0)} className="bg-white border border-slate-200 hover:border-yellow-500 rounded-xl p-4 text-center transition">
                    <p className="font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed', fontSize: '1.1rem' }}>{b}</p>
                    <p className="text-xs text-slate-400">{brandCounts[b] !== undefined ? `${fmt(brandCounts[b])} pièces` : ''}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
